import { ui } from "../../../components/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { type SubmitHandler, useForm } from "react-hook-form";
import * as z from "zod";
import {
    createUniqueShockwaveOptions,
    toFilteredUniqueShockwaveOptions,
} from "../utils/shockwaveOptions";
import useShockwaveDetailsOptions from "../hooks/useShockwaveDetailsOptions";
import Autocomplete from "../../../components/ui/autocomplete";
import Fuse from "fuse.js";
import { Label } from "../../../components/ui/label";
import { useHandleNewShockwaveLocation } from "../api/handleNewShockwaveLocation";
import { useAuth } from "../../../utils/auth";

const formSchema = z.object({
    orthopaedic_area: z.string().min(1, "Required"),
    site: z.string().min(1, "Required"),
    subsite: z.string().min(1, "A specific location is required"),
});

type Props = {
    defaultArea?: string;
    defaultSite?: string;
};

export function ShockwaveLocationForm({ defaultArea, defaultSite }: Props) {
    const auth = useAuth();
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            orthopaedic_area: defaultArea || "",
            site: defaultSite || "",
        },
    });

    const { shockwaveLocations } = useShockwaveDetailsOptions();

    const fetchAreaSuggestions = async (search: string) => {
        const options = createUniqueShockwaveOptions(
            shockwaveLocations,
            "orthopaedic_area",
        ).map(({ value }) => value);

        const fuse = new Fuse(options, {
            threshold: 0.3,
            distance: 100,
        });

        return search
            ? fuse.search(search).map((result) => result.item)
            : options;
    };

    const fetchSiteSuggestions = async (search: string) => {
        const options = toFilteredUniqueShockwaveOptions(
            shockwaveLocations,
            "site",
            "orthopaedic_area",
            form.watch("orthopaedic_area"),
        ).map(({ value }) => value);

        const fuse = new Fuse(options, {
            threshold: 0.3,
            distance: 100,
        });

        return search
            ? fuse.search(search).map((result) => result.item)
            : options;
    };

    const fetchSubsiteSuggestions = async (search: string) => {
        const options = toFilteredUniqueShockwaveOptions(
            shockwaveLocations,
            "subsite",
            "site",
            form.watch("site"),
        ).map(({ value }) => value);

        const fuse = new Fuse(options, {
            threshold: 0.3,
            distance: 100,
        });

        return search
            ? fuse.search(search).map((result) => result.item)
            : options;
    };

    const { mutate: handleNewShockwaveLocation } =
        useHandleNewShockwaveLocation();

    const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (
        values,
    ) => {
        handleNewShockwaveLocation({
            userId: auth.id,
            orthopaedicArea: values.orthopaedic_area,
            shockwaveSite: values.site,
            shockwaveSubsite: values.subsite,
        });
    };

    return (
        <ui.Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div>
                    <Label>Anatomic Area</Label>

                    <Autocomplete
                        fetchSuggestions={fetchAreaSuggestions}
                        options={createUniqueShockwaveOptions(
                            shockwaveLocations,
                            "orthopaedic_area",
                        ).map(({ value }) => value)}
                        value={form.watch("orthopaedic_area")}
                        onChange={(val) =>
                            form.setValue("orthopaedic_area", val)}
                    />
                </div>
                <div>
                    <Label>Shockwave Site</Label>

                    <Autocomplete
                        value={form.watch("site")}
                        onChange={(val) => form.setValue("site", val)}
                        fetchSuggestions={fetchSiteSuggestions}
                        options={toFilteredUniqueShockwaveOptions(
                            shockwaveLocations,
                            "site",
                            "orthopaedic_area",
                            form.watch("orthopaedic_area"),
                        ).map(({ value }) => value)}
                    />
                </div>
                <div>
                    <Label>Shockwave Subsite</Label>
                    <Autocomplete
                        value={form.watch("subsite")}
                        onChange={(val) => form.setValue("subsite", val)}
                        fetchSuggestions={fetchSubsiteSuggestions}
                        options={toFilteredUniqueShockwaveOptions(
                            shockwaveLocations,
                            "subsite",
                            "site",
                            form.watch("site"),
                        ).map(({ value }) => value)}
                    />
                </div>

                <div className="flex justify-end gap-2">
                    <ui.DialogClose>
                        <ui.Button
                            variant="outline"
                            onClick={() => form.reset()}
                        >
                            Cancel
                        </ui.Button>
                    </ui.DialogClose>

                    <ui.DialogClose>
                        <ui.Button type="submit">Submit</ui.Button>
                    </ui.DialogClose>
                </div>
            </form>
        </ui.Form>
    );
}

export default ShockwaveLocationForm;
